/**
 * Rope Manager
 * Manages rope lifecycle and operations
 * Handles loading, clearing, creating, and querying ropes
 */

import { Platform } from '../../../shared/CharacterTypes';
import { gameLogger } from '../../../utils/GameLogger';
// import { RopeEntity, createRope } from '../../entities/Rope';

/**
 * Manages rope lifecycle and operations
 * Handles loading, clearing, creating, and querying ropes
 */
export class RopeManager {
  private static instance: RopeManager;
  private ropePlatforms: Platform[] = [];
  private ropeEntities: any[] = [];

  private constructor() { }

  public static getInstance(): RopeManager {
    if (!RopeManager.instance) {
      RopeManager.instance = new RopeManager();
    }
    return RopeManager.instance;
  }

  /**
   * Load rope configurations from level data
   */
  public loadRopes(levelData?: import('../../../shared/CharacterTypes').PlatformerLevel): void {
    // Clear existing ropes first
    this.clearRopes();

    let levelRopes: Platform[] = [];
    if (levelData && levelData.platforms) {
      levelRopes = levelData.platforms.filter(p => p.type === 'rope');
    }

    this.ropePlatforms = levelRopes;

    gameLogger.info('map', `[RopeManager] Loaded ${this.ropePlatforms.length} ropes`);
    this.ropePlatforms.forEach(r => {
      gameLogger.info('map', `  - Rope ${r.id}: x=${r.x}, y=${r.y}, size=${r.width}x${r.height}`);
    });
  }

  /**
   * Clear all ropes and their entities
   */
  public clearRopes(): void {
    // Clear rope entities from scene
    this.ropeEntities.forEach(entity => {
      if (entity && typeof entity.destroy === 'function') {
        entity.destroy();
      }
    });
    this.ropeEntities = [];
    this.ropePlatforms = [];

    gameLogger.info('map', '[RopeManager] Ropes cleared');
  }

  /**
   * Create rope entities in the scene
   */
  public async createRopesInScene(scene: any): Promise<void> {
    if (!scene) return;

    // Don't clear rope platforms array here, just the entities
    this.ropeEntities.forEach(e => {
      if (e && typeof e.destroy === 'function') e.destroy();
    });
    this.ropeEntities = [];

    try {
      const { createRope } = await import('../../entities/Rope');
      this.ropePlatforms.forEach(ropeData => {
        const rope = createRope(scene, {
          id: ropeData.id,
          x: ropeData.x + ropeData.width / 2, // Center it
          y: ropeData.y + ropeData.height / 2, // Center it
          width: ropeData.width,
          height: ropeData.height,
          color: ropeData.color || '#D2B48C'
        });
        this.ropeEntities.push(rope);
      });
      gameLogger.info('map', `[RopeManager] Created ${this.ropeEntities.length} rope entities`);
    } catch (e) {
      gameLogger.warn('map', '[RopeManager] Failed to create ropes (Rope entity missing?):', e);
    }
  }

  /**
   * Get rope at a specific position
   */
  public getRopeAt(x: number, y: number): Platform | null {
    for (const rope of this.ropePlatforms) {
      const ropeLeft = rope.x;
      const ropeRight = rope.x + rope.width;
      const ropeTop = rope.y;
      const ropeBottom = rope.y + rope.height;

      if (x >= ropeLeft && x <= ropeRight && y >= ropeTop && y <= ropeBottom) {
        return rope;
      }
    }
    return null;
  }

  /**
   * Get all rope platforms
   */
  public getRopePlatforms(): Platform[] {
    return this.ropePlatforms;
  }

  /**
   * Get all rope entities
   */
  public getRopeEntities(): any[] {
    return this.ropeEntities;
  }

  /**
   * Find overlapping rope entity for a character
   */
  public findOverlappingRope(character: { x: number; y: number; width?: number; height?: number }): any | null {
    for (const entity of this.ropeEntities) {
      if (entity && typeof entity.checkOverlap === 'function' && entity.checkOverlap(character)) {
        return entity;
      }
    }
    return null;
  }

  /**
   * Set visual feedback for player attachment
   */
  public setPlayerAttached(attached: boolean, character?: { x: number; y: number; width?: number; height?: number }): void {
    if (!character) {
      // Clear all attachment visuals
      this.ropeEntities.forEach(e => e.setPlayerAttached(false));
      return;
    }

    const overlappingRope = this.findOverlappingRope(character);

    // Clear all first
    this.ropeEntities.forEach(e => {
      if (e !== overlappingRope && typeof e.setPlayerAttached === 'function') {
        e.setPlayerAttached(false);
      }
    });

    // Set attachment on overlapping rope
    if (overlappingRope && typeof overlappingRope.setPlayerAttached === 'function') {
      overlappingRope.setPlayerAttached(attached);
    }
  }
}
